/*
 * The fleet's API tokens screen (`tiketo-cms` @ `c4387239`,
 * `modules-user-management/frontend/components/user-management/APITokensSettings.tsx`),
 * with the differences `design/0003` decided and nothing else.
 *
 * What is the fleet's, unchanged: the header, the create button, the bordered
 * table, the three dialogs — create, "here is your token, copy it now", and the
 * confirm on delete — and the words in all of them, from the fleet's own English
 * (`useTranslation`).
 *
 * Four forced differences, each one a thing that cannot run here rather than a
 * thing we would rather do:
 *
 *   1. **The data layer.** The fleet reads AppSync through
 *      `useApiTokensQueryOptions` and writes through two mutation-option hooks,
 *      and takes its workspace from `useSuspenseUserData`. Neither exists here:
 *      every read goes through the Worker to `sitebrew-api` as REST, and the
 *      workspace is in the address (design note 0001 §2). Same shape as
 *      `DataTable`'s deviation — the fleet's state stays, what it is handed to
 *      is ours.
 *   2. **No toasts.** The fleet says everything with `sonner`, which is not
 *      installed here (`LayoutNotice` records the same decision). A refusal is
 *      shown where it happened: in the dialog that caused it, in the API's own
 *      words.
 *   3. **Expiry** — the one addition `design/0003` names: an `Expires` column
 *      and a choice in the create dialog, because `expires_at` is in our data
 *      model and Jakub's decision of 15 August. Written as a gap in the fleet
 *      rather than as our column: landing it upstream is DevGuru's and Jakub's
 *      path, and this file is the shape.
 *   4. **A revoked key stays in the list** (`design/0003`) — the fleet deletes
 *      the row. So a row has to be able to say it is spent, or the screen shows
 *      a dead key with a working bin beside it.
 *
 * And one thing the fleet's screen does not have to say, because its keys open
 * an events API rather than a workspace of live websites: the four sentences of
 * *The console screen* in `design/0003`. They are on the screen, not in a note,
 * because every one of them is learned the hard way otherwise.
 */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useTranslation, type StringKey } from "@/hooks/useTranslation";
import { ApiError } from "@/lib/api";
import { useMe } from "@/lib/session";
import {
  creatorLabel,
  DEFAULT_EXPIRY_CHOICE,
  EXPIRY_CHOICES,
  expiryFrom,
  tokenState,
  useCreateToken,
  useRevokeToken,
  useTokens,
  type IssuedToken,
  type Token,
} from "@/lib/tokens";
import { formatDistanceToNow } from "date-fns";
import { Copy, Key, Trash2 } from "lucide-react";
import { useState } from "react";

export function APITokensSettings({ workspaceId }: { workspaceId: string }) {
  const { t } = useTranslation();
  const me = useMe();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [tokenName, setTokenName] = useState("");
  const [expiryChoice, setExpiryChoice] = useState<string>(DEFAULT_EXPIRY_CHOICE);
  const [nameError, setNameError] = useState<string | null>(null);
  const [createdToken, setCreatedToken] = useState<IssuedToken | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");
  const [tokenToDelete, setTokenToDelete] = useState<{ id: string; name: string } | null>(null);

  const { data, isLoading, error } = useTokens(workspaceId);

  const createToken = useCreateToken(workspaceId);
  const revokeToken = useRevokeToken(workspaceId);

  /*
   * What a failure is allowed to say.
   *
   * `api()` builds an `ApiError` out of a non-2xx answer and nothing else, so
   * everything the browser throws on the way there — a dropped connection is
   * `TypeError: Failed to fetch` — arrives here as an error whose `message` was
   * written for a developer. Drawing it raw is how "the API's own words" turns
   * into `Failed to fetch` on somebody's screen; dropping it, which is what the
   * sister screens do (`site.tsx` narrows and then renders nothing), is the
   * silence this screen has already been caught in once. So the narrowing has a
   * *fallback sentence* rather than a `null`, and the fallback is the fleet's
   * own (`createError` / `deleteError`), which exists for exactly this case —
   * the fleet reaches for it whenever its mutation rejects.
   */
  const said = (failure: unknown, fallback: StringKey) =>
    failure instanceof ApiError ? failure.message : t(fallback);

  /*
   * A message drawn *inside* a dialog has the lifetime of that dialog, and a
   * lifetime is two ends. `preventDefault` on the confirm closed the early one
   * — a refusal that vanished with the click that caused it. This closes the
   * late one: `revokeToken.error`, `createToken.error` and `nameError` live in
   * the screen, not in the dialog Radix unmounts, so without this a refusal
   * about key A is already on screen when the bin of key B opens its dialog,
   * before anybody has pressed anything.
   *
   * Cleared **on open only**, and the closing edge is deliberately empty. The
   * first version cleared both, which reads as the more careful choice and is
   * not: `reset()` is `removeObserver` on the mutation
   * (`@tanstack/query-core/build/modern/mutationObserver.js`, installed
   * `5.101.4`), and the observer is what delivers the `onSuccess` handed to
   * `mutate`. The request itself is not cancelled — `Mutation.execute` runs on
   * and the hook's own `onSuccess` still invalidates the listing — so closing
   * this dialog with Escape while the POST is in flight would have issued a
   * key, drawn its row, and never opened the dialog holding its value. The API
   * shows that value once (design note 0003 §6); nothing recovers it.
   *
   * Clearing on open is enough for what the closing edge was there for: a
   * refusal can only be read while a dialog is open, so resetting as one opens
   * covers both ends of the lifetime by itself (`sitebrew-console#11` round 2,
   * DevGuru).
   */
  const openCreateDialog = (open: boolean) => {
    if (open) {
      createToken.reset();
      setNameError(null);
    }
    setIsCreateDialogOpen(open);
  };

  const openDeleteDialog = (token: { id: string; name: string } | null) => {
    if (token) revokeToken.reset();
    setTokenToDelete(token);
  };

  const handleCreateToken = () => {
    if (!tokenName.trim()) {
      setNameError(t("apiTokens.messages.nameRequired"));
      return;
    }
    setNameError(null);
    createToken.mutate(
      { name: tokenName.trim(), expiresAt: expiryFrom(expiryChoice) },
      {
        onSuccess: (token) => {
          // In this order, and both before the value is shown: the dialog that
          // is closing still holds what was typed, and it must not be there the
          // next time the dialog opens.
          setTokenName("");
          setExpiryChoice(DEFAULT_EXPIRY_CHOICE);
          setIsCreateDialogOpen(false);
          setCopyState("idle");
          setCreatedToken(token);
        },
      },
    );
  };

  const handleDeleteToken = () => {
    if (!tokenToDelete) return;
    revokeToken.mutate(tokenToDelete.id, { onSuccess: () => setTokenToDelete(null) });
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyState("copied");
    } catch {
      // Said on the screen rather than swallowed: the whole point of this dialog
      // is that the value is not recoverable, so a copy that silently did
      // nothing is the one failure that cannot be undone by opening it again.
      setCopyState("failed");
    }
  };

  const tokens = data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium">{t("apiTokens.title")}</h3>
        <p className="text-sm text-muted-foreground">{t("apiTokens.description")}</p>
      </div>

      {/*
        The four sentences of `design/0003`. On the screen and above the table,
        because two of them are about what the key in the row can do and one of
        them — deleting a site retires its address for good — is the only
        irreversible thing anybody can reach from here.
      */}
      <div className="rounded-lg border bg-muted/40 p-4 text-sm">
        <p className="font-medium">{t("apiTokens.grants.title")}</p>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
          <li>{t("apiTokens.grants.once")}</li>
          <li>{t("apiTokens.grants.fullAccess")}</li>
          <li>{t("apiTokens.grants.irreversible")}</li>
          <li>{t("apiTokens.grants.creator")}</li>
        </ul>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={() => openCreateDialog(true)}
          className="flex items-center gap-2"
        >
          <Key className="h-4 w-4" />
          {t("apiTokens.createButton")}
        </Button>
      </div>

      {/*
        A refetch that failed while the previous answer is still in hand: the
        rows below are real keys and stay, but the screen has to say it is not
        looking at the newest list — otherwise a revocation that did not go
        through reads as a listing that has not changed yet.
      */}
      {error && data ? (
        <p role="alert" className="text-sm text-destructive">
          {said(error, "apiTokens.messages.loadError")}
        </p>
      ) : null}

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("apiTokens.table.name")}</TableHead>
              <TableHead>{t("apiTokens.table.createdBy")}</TableHead>
              <TableHead>{t("apiTokens.table.created")}</TableHead>
              <TableHead>{t("apiTokens.table.lastUsed")}</TableHead>
              <TableHead>{t("apiTokens.table.expires")}</TableHead>
              <TableHead className="text-right">{t("apiTokens.table.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading || (error && !data) || tokens.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground">
                  {/*
                    The API's own sentence when the list failed, rather than
                    "No API tokens created yet" — which is a different fact and
                    the one somebody would act on by issuing a second key they
                    already have.

                    `error && !data`, not `error`: a failed *refetch* leaves the
                    previous answer in hand, and blanking the table then hides
                    keys somebody still holds — the notice above says what went
                    wrong without taking the rows away.
                  */}
                  {isLoading
                    ? "Loading..."
                    : error
                      ? said(error, "apiTokens.messages.loadError")
                      : t("apiTokens.noTokens")}
                </TableCell>
              </TableRow>
            ) : (
              tokens.map((token) => (
                <TokenRow
                  key={token.id}
                  token={token}
                  createdBy={creatorLabel(token, me.data?.user)}
                  onDelete={() => openDeleteDialog({ id: token.id, name: token.name })}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create Token Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={openCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("apiTokens.create.title")}</DialogTitle>
            <DialogDescription>{t("apiTokens.create.description")}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                {t("apiTokens.create.nameLabel")}
              </Label>
              <Input
                id="name"
                value={tokenName}
                onChange={(e) => setTokenName(e.target.value)}
                className="col-span-3"
                placeholder={t("apiTokens.create.namePlaceholder")}
              />
            </div>
            {/*
              The choice `design/0003` adds. `Never` is in the list rather than
              implied by leaving the field alone: the visibility of "forever" is
              the half of Jakub's decision that makes it a choice instead of a
              drift.
            */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="expires" className="text-right">
                {t("apiTokens.create.expiryLabel")}
              </Label>
              <Select value={expiryChoice} onValueChange={setExpiryChoice}>
                <SelectTrigger id="expires" className="col-span-3">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EXPIRY_CHOICES.map((choice) => (
                    <SelectItem key={choice.id} value={choice.id}>
                      {choice.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {nameError || createToken.error ? (
              <p role="alert" className="text-sm text-destructive">
                {nameError ?? said(createToken.error, "apiTokens.messages.createError")}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="submit"
              onClick={handleCreateToken}
              disabled={createToken.isPending}
            >
              {createToken.isPending
                ? t("apiTokens.create.creating")
                : t("apiTokens.create.createButton")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Token Created Dialog */}
      <Dialog
        open={!!createdToken}
        onOpenChange={(open) => !open && setCreatedToken(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("apiTokens.created.title")}</DialogTitle>
            <DialogDescription>{t("apiTokens.created.description")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-muted rounded-md font-mono text-sm break-all">
              {createdToken?.value}
            </div>
            <Button
              onClick={() => copyToClipboard(createdToken?.value || "")}
              className="w-full flex items-center gap-2"
            >
              <Copy className="h-4 w-4" />
              {t("apiTokens.created.copyButton")}
            </Button>
            {copyState === "idle" ? null : (
              <p
                role="status"
                className={copyState === "copied" ? "text-sm text-muted-foreground" : "text-sm text-destructive"}
              >
                {copyState === "copied"
                  ? t("apiTokens.messages.copySuccess")
                  : t("apiTokens.messages.copyError")}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setCreatedToken(null)}>
              {t("apiTokens.created.done")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!tokenToDelete}
        onOpenChange={(open: boolean) => !open && openDeleteDialog(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("apiTokens.delete.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("apiTokens.delete.description", { name: tokenToDelete?.name ?? "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {revokeToken.error ? (
            <p role="alert" className="text-sm text-destructive">
              {said(revokeToken.error, "apiTokens.messages.deleteError")}
            </p>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel>{t("apiTokens.delete.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                /*
                 * Radix closes the dialog on this button by itself, which is
                 * harmless in the fleet: a refusal arrives there as a toast that
                 * outlives the dialog it came from. Here there are no toasts and
                 * the message is drawn *inside* this dialog, so letting the
                 * click close it means a refused revocation says nothing at all
                 * — the key stays live and the only thing that happened is that
                 * a dialog went away.
                 *
                 * So closing becomes ours, and happens on success only
                 * (`handleDeleteToken`). `test/api-tokens.test.tsx` holds it:
                 * with this line removed, the refusal case goes red.
                 */
                event.preventDefault();
                handleDeleteToken();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("apiTokens.delete.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * One key.
 *
 * Its own component because a row has three states and the fleet's has one: a
 * revoked key stays in our listing (`design/0003` — a key that vanishes takes
 * the answer to "was this the one that leaked" with it), and an expired one is
 * dead without anybody having done anything. Both have to read as spent, and
 * the bin has to go with the revoked one, or the screen offers an action whose
 * only outcome is nothing happening.
 */
function TokenRow({
  token,
  createdBy,
  onDelete,
}: {
  token: Token;
  createdBy: string;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const state = tokenState(token);

  return (
    <TableRow className={state === "live" ? undefined : "text-muted-foreground"}>
      <TableCell className="font-medium">
        <span className="flex items-center gap-2">
          {token.name}
          {state === "live" ? null : (
            <Badge variant="secondary">
              {state === "revoked" ? t("apiTokens.state.revoked") : t("apiTokens.state.expired")}
            </Badge>
          )}
        </span>
        {/*
          The prefix, which is what somebody holding a key can match against a
          row without the console ever seeing the value again (`design/0003`:
          display only, nothing is looked up by it).
        */}
        <span className="block font-mono text-xs text-muted-foreground">{token.prefix}</span>
      </TableCell>
      <TableCell>{createdBy}</TableCell>
      <TableCell>{ago(token.createdAt)}</TableCell>
      <TableCell>{token.lastUsedAt ? ago(token.lastUsedAt) : t("apiTokens.table.never")}</TableCell>
      <TableCell>
        {token.expiresAt === null ? t("apiTokens.table.never") : ago(token.expiresAt)}
      </TableCell>
      <TableCell className="text-right">
        {state === "revoked" ? null : (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="text-destructive hover:text-destructive"
            aria-label={`${t("apiTokens.delete.confirm")} ${token.name}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

/**
 * The fleet's way of writing a moment: relative, with a suffix, in both
 * directions — "3 days ago" and "in 3 months" come out of the same call, which
 * is why the `Expires` column can use it too.
 */
const ago = (at: number) => formatDistanceToNow(new Date(at), { addSuffix: true });
